import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, X } from 'lucide-react';
import ChatPanel from './ChatPanel';
import { useAudience } from '../../contexts/AudienceContext';
import { matchIntent } from '@/lib/chatEngine';
import { askAssistant, fetchChatConfig } from '@/lib/chatApi';
import { trackEvent } from '@/lib/trackEvent';
import {
  ACTIONS,
  ESCALATION,
  FALLBACK,
  GREETING,
  WHATSAPP_NUMBER,
  buildPricingAnswer,
} from '@/data/chatKnowledge';
import {
  clearTranscript,
  hasSeenNudge,
  loadTranscript,
  markNudgeSeen,
  saveTranscript,
} from '@/lib/chatStorage';

const NUDGE_DELAY_MS = 7000;
/** Two unanswered questions in a row is the point where a human is more useful than more chips. */
const ESCALATE_AFTER_MISSES = 2;
/** Enough of a pause that the typing indicator reads as thinking rather than flickering. */
const MIN_THINKING_MS = 320;

let messageSeq = 0;
const nextId = () => `m${Date.now().toString(36)}${(messageSeq += 1)}`;

const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

function buildGreeting(audience) {
  const g = audience === 'school' ? GREETING.school : GREETING.student;
  return { id: nextId(), role: 'assistant', text: g.text, chips: g.chips, actions: [] };
}

const ChatWidget = () => {
  const { audience } = useAudience();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => loadTranscript());
  const [isTyping, setIsTyping] = useState(false);
  const [streamingId, setStreamingId] = useState(null);
  const [showNudge, setShowNudge] = useState(false);

  const missStreakRef = useRef(0);
  const fabRef = useRef(null);
  /** Bumped on every send and on reset so a slow in-flight reply cannot land out of order. */
  const generationRef = useRef(0);
  const historyRef = useRef([]);
  const plansRef = useRef([]);
  const configLoadedRef = useRef(false);

  useEffect(() => {
    if (messages.length === 0) setMessages([buildGreeting(audience)]);
  }, [audience, messages.length]);

  useEffect(() => {
    if (messages.length) saveTranscript(messages);
    historyRef.current = messages.slice(-4).map((m) => ({ role: m.role, text: m.text }));
  }, [messages]);

  useEffect(() => {
    if (open || hasSeenNudge()) return undefined;
    const t = window.setTimeout(() => setShowNudge(true), NUDGE_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [open]);

  const dismissNudge = useCallback(() => {
    setShowNudge(false);
    markNudgeSeen();
  }, []);

  const pushMessage = useCallback((message) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  const emit = useCallback(
    (generation, { text, chips = [], actions = [], answeredBy }) => {
      if (generationRef.current !== generation) return;
      const id = nextId();
      setIsTyping(false);
      setStreamingId(id);
      pushMessage({ id, role: 'assistant', text, chips, actions, answeredBy });
    },
    [pushMessage],
  );

  /**
   * Knowledge base first, assistant only on a miss. That ordering is what keeps the running
   * cost near zero: the common questions never leave the browser.
   */
  const respond = useCallback(
    async (question, source) => {
      const generation = (generationRef.current += 1);
      const result = matchIntent(question, audience);
      setIsTyping(true);

      if (result.matched) {
        missStreakRef.current = 0;
        const text = result.dynamic === 'pricing' ? buildPricingAnswer(plansRef.current) : result.text;
        void trackEvent('chat_message_sent', {
          meta: { surface: 'chat_widget', audience, source, intent: result.intentId, answeredBy: 'kb' },
        });
        if (result.escalate) {
          void trackEvent('chat_escalation_shown', {
            meta: { surface: 'chat_widget', audience, intent: result.intentId },
          });
        }
        await sleep(380 + Math.min(500, text.length * 2));
        emit(generation, {
          text,
          chips: result.chips,
          actions: result.actions,
          answeredBy: 'kb',
        });
        return;
      }

      missStreakRef.current += 1;
      const startedAt = Date.now();
      const ai = await askAssistant({ message: question, audience, history: historyRef.current });
      const elapsed = Date.now() - startedAt;
      if (elapsed < MIN_THINKING_MS) await sleep(MIN_THINKING_MS - elapsed);
      if (generationRef.current !== generation) return;

      if (ai.ok) {
        missStreakRef.current = 0;
        const escalate = ai.suggestEscalation;
        void trackEvent('chat_message_sent', {
          meta: { surface: 'chat_widget', audience, source, intent: 'llm', answeredBy: 'ai' },
        });
        if (escalate) {
          void trackEvent('chat_escalation_shown', {
            meta: { surface: 'chat_widget', audience, intent: 'llm' },
          });
        }
        emit(generation, {
          text: ai.answer,
          actions: escalate ? ESCALATION.actions : [],
          answeredBy: 'ai',
        });
        return;
      }

      const escalate = missStreakRef.current >= ESCALATE_AFTER_MISSES;
      void trackEvent('chat_message_sent', {
        meta: { surface: 'chat_widget', audience, source, intent: 'fallback', answeredBy: 'fallback', reason: ai.code },
      });
      if (escalate) {
        void trackEvent('chat_escalation_shown', {
          meta: { surface: 'chat_widget', audience, intent: 'fallback' },
        });
      }
      emit(generation, {
        text: escalate ? `${FALLBACK.text}\n\n${ESCALATION.text}` : FALLBACK.text,
        chips: escalate ? [] : FALLBACK.chips,
        actions: escalate ? ESCALATION.actions : [],
        answeredBy: 'fallback',
      });
    },
    [audience, emit],
  );

  const handleSend = useCallback(
    (text, source = 'typed') => {
      pushMessage({ id: nextId(), role: 'user', text });
      void respond(text, source);
    },
    [pushMessage, respond],
  );

  const handleAction = useCallback(
    (actionId) => {
      void trackEvent('chat_action_clicked', {
        meta: { surface: 'chat_widget', audience, action: actionId },
      });

      if (actionId.startsWith('scroll:')) {
        const targetId = actionId.slice('scroll:'.length);
        setOpen(false);
        window.setTimeout(() => {
          document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
        }, 180);
        return;
      }

      switch (actionId) {
        case ACTIONS.WHATSAPP: {
          const message =
            audience === 'school'
              ? 'Hi ChooseRightCareer, I want to discuss a school partnership or book a demo.'
              : 'Hi ChooseRightCareer, I have a question about Career DNA.';
          const href = `https://wa.me/${WHATSAPP_NUMBER.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`;
          window.open(href, '_blank', 'noopener,noreferrer');
          break;
        }
        case ACTIONS.START:
          navigate('/start');
          break;
        case ACTIONS.DEMO:
          navigate('/try');
          break;
        case ACTIONS.COUNSELLORS:
          navigate('/counsellors');
          break;
        case ACTIONS.PRIVACY:
          navigate('/privacy-policy');
          break;
        default:
          break;
      }
    },
    [audience, navigate],
  );

  const handleReset = useCallback(() => {
    generationRef.current += 1;
    missStreakRef.current = 0;
    setIsTyping(false);
    setStreamingId(null);
    clearTranscript();
    setMessages([buildGreeting(audience)]);
    void trackEvent('chat_reset', { meta: { surface: 'chat_widget', audience } });
  }, [audience]);

  const handleStreamDone = useCallback((id) => {
    setStreamingId((current) => (current === id ? null : current));
  }, []);

  const openChat = useCallback(() => {
    dismissNudge();
    setOpen(true);
    void trackEvent('chat_opened', { meta: { surface: 'chat_widget', audience } });
    /* Deferred to first open so the landing page never pays for a request nobody needed. */
    if (!configLoadedRef.current) {
      configLoadedRef.current = true;
      void fetchChatConfig().then((config) => {
        plansRef.current = config.plans;
      });
    }
  }, [audience, dismissNudge]);

  const closeChat = useCallback(() => {
    setOpen(false);
    void trackEvent('chat_closed', {
      meta: { surface: 'chat_widget', audience, turns: messages.filter((m) => m.role === 'user').length },
    });
    window.setTimeout(() => fabRef.current?.focus(), 0);
  }, [audience, messages]);

  return (
    <>
      {open && (
        <button
          type="button"
          aria-label="Close chat"
          onClick={closeChat}
          className="fixed inset-0 z-[65] cursor-default bg-gray-900/40 sm:hidden"
        />
      )}

      {open && (
        <ChatPanel
          audience={audience}
          messages={messages}
          isTyping={isTyping}
          streamingId={streamingId}
          onClose={closeChat}
          onSend={handleSend}
          onChip={(chip) => handleSend(chip, 'chip')}
          onAction={handleAction}
          onReset={handleReset}
          onStreamDone={handleStreamDone}
        />
      )}

      {/* Sits directly above the WhatsApp FAB (bottom-6, h-14) with a 0.75rem gap. Lower z-index
          than FloatingHelp so its popover cleanly covers this button instead of clashing with it. */}
      <div
        className={`fixed bottom-[5.75rem] right-6 z-[55] ${open ? 'hidden sm:block' : 'block'}`}
      >
        {showNudge && !open && (
          <div className="absolute bottom-1 right-full mr-3 w-max max-w-[220px] animate-in fade-in slide-in-from-right-2 rounded-2xl rounded-br-md border border-gray-100 bg-white px-3.5 py-2.5 shadow-xl">
            <button
              type="button"
              onClick={dismissNudge}
              aria-label="Dismiss"
              className="absolute -right-1.5 -top-1.5 rounded-full border border-gray-200 bg-white p-0.5 text-gray-400 hover:text-gray-600"
            >
              <X className="h-3 w-3" />
            </button>
            <p className="text-[12.5px] font-medium leading-snug text-gray-700">
              Questions about fees or timing? Ask me — instant answers.
            </p>
          </div>
        )}

        <button
          ref={fabRef}
          type="button"
          onClick={open ? closeChat : openChat}
          aria-expanded={open}
          aria-label={open ? 'Close career assistant chat' : 'Open career assistant chat'}
          className="relative flex h-14 w-14 items-center justify-center rounded-full bg-white text-blue-700 shadow-lg ring-1 ring-gray-200 transition-all duration-300 hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-300/50"
        >
          {open ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
          {!open && (
            <span className="absolute right-0.5 top-0.5 flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-teal-500" />
            </span>
          )}
        </button>
      </div>
    </>
  );
};

export default ChatWidget;
